/**
 * 导航模型 —— 三个「界面」的真正差别就在这张表里。
 *
 * 后端是**同一套 API**，权限完全由组织在树里的位置决定
 * （console-api `lib/authz.ts` 里没有一处 `if (isPlatform)` 散落在业务代码中）。
 * 前端保持同样的结构：不做三个应用，只做一张按 `org.type` 过滤的导航表。
 *
 * 这不只是省事。三套前端意味着同一份越权判断被抄三遍，
 * 而四方平台最容易出事故的地方恰恰就是「某一套忘了加那一行判断」。
 *
 * `visible` 只决定**看不看得见**。真正的授权在服务端，
 * 前端隐藏一个入口从来不是安全措施，只是别让人点了才被 403。
 */

import type { OrgRole, OrgType } from './types.ts';
import { roleAtLeast } from './format.ts';

export interface NavItem {
  readonly path: string;
  readonly label: string;
  readonly group: string;
  readonly types: readonly OrgType[];
  readonly minRole?: OrgRole;
  /**
   * 该页面对「按通道收窄可见范围」的成员是否不可用。
   *
   * 标它的判据只有一条：**这个页面的数据既没有通道维度、也无法按商户收窄**，
   * 服务端对受限成员整页 403。这里只是别让人点了才吃错误 —— 隐藏入口从来不是安全措施。
   *
   * 目前没有页面标它：资金三页（结算批次、账本、提现）曾经标着，0022 分组之后改为
   * 按商户收窄后开放（见 console-api 的 channelScopedReadOnlyMw）。
   */
  readonly hiddenWhenChannelScoped?: boolean;
  /**
   * 该页面对「看不到钱」的成员是否不可用。
   *
   * 标它的判据只有一条：**这个页面去掉金额之后就不剩什么了**。费率、账本、
   * 结算批次、提现全属此类，服务端对这类成员一律 403（见 notMoneyHiddenMw）。
   *
   * 交易 / 退款 / 争议 / 总览刻意**不标**：那些去掉利润仍然成立（成败、结没结、
   * 成功率、拒付分布都还在），正是这类成员的主场。
   */
  readonly hiddenWhenMoneyHidden?: boolean;
}

const ALL: readonly OrgType[] = ['platform', 'agent', 'merchant'];

export const NAV_ITEMS: readonly NavItem[] = [
  { path: 'overview', label: '总览', group: '经营', types: ALL },
  { path: 'payments', label: '交易', group: '经营', types: ALL },
  { path: 'refunds', label: '退款', group: '经营', types: ALL },
  { path: 'disputes', label: '争议', group: '经营', types: ALL },
  // 收款单与收款链接都只对商户有意义（钱要落到某个商户账上），但上级要能替下级开与查看。
  // 两者的区别是「收一笔就关」与「长期挂着反复收」，标签必须让人一眼分清 ——
  // 之前只有前者时它叫「收款链接」，现在那个名字归给真正可复用的那个。
  { path: 'invoices', label: '收款单', group: '经营', types: ALL },
  { path: 'payment_links', label: '收款链接', group: '经营', types: ALL },

  // 平台与代理才有「下级」这个概念；商户是叶子节点
  { path: 'orgs', label: '组织', group: '渠道', types: ['platform', 'agent'] },
  { path: 'channels', label: '通道账户', group: '渠道', types: ['platform', 'agent'] },
  /*
   * 分组是**平台内部**的可见性配置单位，代理看不到也用不上：
   * 代理能看哪些通道由 grants 决定，与这张表无关。
   *
   * 不标 hiddenWhenChannelScoped —— 服务端对受限成员已经是 403（写接口）
   * 且列表里只有组名与计数，没有金额。但受限成员本来也配不了分组，
   * 入口留着只会让人点了吃错误，所以 minRole 卡在 admin。
   */
  { path: 'channel_groups', label: '通道分组', group: '渠道', types: ['platform'], minRole: 'admin' },
  // 授权是平台把通道发给代理的动作，代理只能看自己的（列表接口对它们都开）
  { path: 'grants', label: '通道授权', group: '渠道', types: ['platform', 'agent'] },
  { path: 'bindings', label: 'MID 下发', group: '渠道', types: ['platform', 'agent'] },
  { path: 'fees', label: '费率', group: '渠道', types: ALL, hiddenWhenMoneyHidden: true },

  // 「资金」整组对按通道收窄的成员**开放但收窄**：这三个页面按商户切，只给他看
  // 「全部通道都在他范围内」的商户（见 console-api 的 visibleMerchantIds）。
  // 曾经整组隐藏 —— 那对只管两三条通道的岗位成立，但分组成员的本职就是看本组商户的结算。
  // 遮金额的成员照旧整组隐藏：那是另一条独立的收窄，去掉金额这三页就不剩什么了。
  { path: 'ledger', label: '账本', group: '资金', types: ALL, hiddenWhenMoneyHidden: true },
  { path: 'settlements', label: '结算批次', group: '资金', types: ALL, hiddenWhenMoneyHidden: true },
  { path: 'payouts', label: '提现', group: '资金', types: ALL, hiddenWhenMoneyHidden: true },

  { path: 'credentials', label: 'API 凭证', group: '接入', types: ALL, minRole: 'admin' },
  // 出站 webhook 只有商户有：密钥是按 merchant_org_id 派生的，
  // 给代理/平台返回一把密钥等于给一把永远不会被用来签任何东西的字符串
  { path: 'webhook', label: 'Webhook', group: '接入', types: ['merchant'], minRole: 'admin' },

  { path: 'members', label: '成员', group: '设置', types: ALL },
  { path: 'profile', label: '我的账号', group: '设置', types: ALL },
];

export function visibleNav(
  orgType: OrgType,
  role: OrgRole,
  channelScoped = false,
  moneyHidden = false,
  /**
   * 页面白名单。`null` = 不受限。
   *
   * 与上面三个参数**方向相反**：它们是「命中就藏」，这个是「不在名单里就藏」。
   * 所以判断写成 `!pageScope.includes(path)`，不是 `pageScope.includes(path)` ——
   * 写反了效果是「只有名单里的页面看不到」，正好倒过来，而且一眼看不出来。
   *
   * `profile` 永远保留：它是改密与登出的入口，藏掉等于把人锁在里面。
   */
  pageScope: readonly string[] | null = null,
): readonly NavItem[] {
  return NAV_ITEMS.filter(
    (item) =>
      item.types.includes(orgType) &&
      (item.minRole === undefined || roleAtLeast(role, item.minRole)) &&
      !(channelScoped && item.hiddenWhenChannelScoped === true) &&
      !(moneyHidden && item.hiddenWhenMoneyHidden === true) &&
      (pageScope === null || item.path === 'profile' || pageScope.includes(item.path)),
  );
}

/** 导航分组的展示顺序。 */
export const NAV_GROUPS: readonly string[] = ['经营', '渠道', '资金', '接入', '设置'];

export function defaultPath(): string {
  return 'overview';
}
